import Image, { StaticImageData } from "next/image";
import { useLocale, useTranslations } from "next-intl";
import {
    THAirPlanFlyingFruits,
    THAnagram,
    THCompleteTheSentence,
    THFindTheMatch,
    THFlappyBird,
    THFlipCards,
    THGameshowQuiz,
    THSituation,
    THWhackAMole,
    ENAirPlanFlyingFruits,
    ENAnagram,
    ENCompleteTheSentence,
    ENFindTheMatch,
    ENFlappyBird,
    ENFlipCards,
    ENGameshowQuiz,
    ENSituation,
    ENWhackAMole,
} from "../../assets";
import { CANONICAL_GAME_TYPE_TITLES, getGameTypeLabel, type GameTypeApiKey } from "../../utils/gameTypeLabels";

type GameCard = {
    key: string;
    gameTypeKey: GameTypeApiKey;
    thImage: StaticImageData;
    enImage: StaticImageData;
};

type CreateGameProps = {
    onSelectTemplate: (title: string) => void;
};

const gameCards: GameCard[] = [
    {
        key: "find-the-match",
        gameTypeKey: "find_the_match",
        thImage: THFindTheMatch,
        enImage: ENFindTheMatch,
    },
    {
        key: "gameshow-quiz",
        gameTypeKey: "game_show_quiz",
        thImage: THGameshowQuiz,
        enImage: ENGameshowQuiz,
    },
    {
        key: "flip-cards",
        gameTypeKey: "flip_cards",
        thImage: THFlipCards,
        enImage: ENFlipCards,
    },
    {
        key: "airplan-flying-fruits",
        gameTypeKey: "air_plan/flying_fruits",
        thImage: THAirPlanFlyingFruits,
        enImage: ENAirPlanFlyingFruits,
    },
    {
        key: "flappy-bird",
        gameTypeKey: "flappy_bird",
        thImage: THFlappyBird,
        enImage: ENFlappyBird,
    },
    {
        key: "whack-a-mole",
        gameTypeKey: "whack_a_mole",
        thImage: THWhackAMole,
        enImage: ENWhackAMole,
    },
    {
        key: "anagram",
        gameTypeKey: "anagram",
        thImage: THAnagram,
        enImage: ENAnagram,
    },
    {
        key: "complete-the-sentence",
        gameTypeKey: "complete_the_sentence",
        thImage: THCompleteTheSentence,
        enImage: ENCompleteTheSentence,
    },
    {
        key: "situation",
        gameTypeKey: "situation",
        thImage: THSituation,
        enImage: ENSituation,
    },
];

export default function CreateGame({ onSelectTemplate }: CreateGameProps) {
    const locale = useLocale();
    const tMain = useTranslations("main");
    const isThaiLocale = locale === "th";

    return (
        <div
            className="grid w-max max-w-full justify-start gap-x-5 gap-y-5 px-2 grid-cols-[repeat(2,minmax(0,13rem))] sm:grid-cols-[repeat(3,minmax(0,14rem))] md:grid-cols-[repeat(3,minmax(0,16rem))] lg:grid-cols-[repeat(3,minmax(0,26rem))]"
        >
            {gameCards.map((card) => {
                const image = isThaiLocale ? card.thImage : card.enImage;
                const label = getGameTypeLabel(card.gameTypeKey, (messageKey) => tMain(messageKey));
                const canonicalTitle = CANONICAL_GAME_TYPE_TITLES[card.gameTypeKey];

                return (
                <button
                    key={card.key}
                    type="button"
                    onClick={() => onSelectTemplate(canonicalTitle)}
                    className="w-full cursor-pointer overflow-hidden rounded-lg"
                    aria-label={label}
                >
                    <Image
                        src={image}
                        alt={label}
                        width={image.width}
                        height={image.height}
                        className="h-auto w-full object-contain"
                        sizes="(max-width: 639px) 50vw, (max-width: 1023px) 34vw, (max-width: 1200px) 30vw, 480px"
                        quality={100}
                    />
                </button>
            )})}
        </div>
    );
}
